import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ChatHistoryItem } from "../../contexts/chat/type";
import ReactMarkdown from "react-markdown";
import { useState } from "react";

interface ChatHistoryModalProps {
  open: boolean;
  onClose: () => void;
  historyItem: ChatHistoryItem | null;
  onLoadToChat: (item: ChatHistoryItem) => void;
}

export const ChatHistoryModal = ({
  open,
  onClose,
  historyItem,
  onLoadToChat,
}: ChatHistoryModalProps) => {
  const [copySuccess, setCopySuccess] = useState(false);

  if (!historyItem) return null;

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const exportAsMarkdown = () => {
    const markdown = generateMarkdown(historyItem);
    const blob = new Blob([markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chat-${historyItem.message_id.substring(0, 8)}-${new Date(historyItem.timestamp).toISOString().split("T")[0]}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const generateMarkdown = (item: ChatHistoryItem): string => {
    let md = `# Chat Conversation\n\n`;
    md += `**Date:** ${formatTimestamp(item.timestamp)}\n`;
    md += `**User:** ${item.user_email}\n`;
    md += `**Iterations:** ${item.iterations}\n\n`;
    md += `---\n\n`;

    md += `## Question\n\n${item.query}\n\n`;

    md += `## Response\n\n${item.response}\n\n`;

    if (item.sql_queries && item.sql_queries.length > 0) {
      md += `## SQL Queries Executed\n\n`;
      item.sql_queries.forEach((sq, i) => {
        md += `### Query ${i + 1}\n\n`;
        if (sq.sql) {
          md += `\`\`\`sql\n${sq.sql}\n\`\`\`\n\n`;
        }
        md += `**Rows returned:** ${sq.row_count}\n\n`;
        if (sq.summary) {
          md += `**Summary:** ${sq.summary}\n\n`;
        }
      });
    }

    if (item.doc_searches && item.doc_searches.length > 0) {
      md += `## Documentation Searches\n\n`;
      item.doc_searches.forEach((ds, i) => {
        md += `${i + 1}. **${ds.question}**\n`;
        if (ds.table_names && ds.table_names.length > 0) {
          md += `   Tables found: ${ds.table_names.join(", ")}\n`;
        }
        md += `\n`;
      });
    }

    if (item.charts && item.charts.length > 0) {
      md += `## Charts Generated\n\n`;
      item.charts.forEach((chart, i) => {
        md += `${i + 1}. **${chart.chart_type || "Chart"}** - ${chart.status}\n`;
      });
    }

    md += `\n---\n\n*Exported from Atomic Assistant*\n`;
    return md;
  };

  const copyToClipboard = async () => {
    const markdown = generateMarkdown(historyItem);
    try {
      await navigator.clipboard.writeText(markdown);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleLoadToChat = () => {
    onLoadToChat(historyItem);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: "80vh" },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <Box>
          <Typography variant="h6" component="span">
            Conversation Details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {formatTimestamp(historyItem.timestamp)}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Stats chips */}
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          <Chip label={`${historyItem.iterations} iterations`} size="small" variant="outlined" />
          {historyItem.total_sql_queries > 0 && (
            <Chip label={`${historyItem.total_sql_queries} SQL queries`} size="small" color="info" variant="outlined" />
          )}
          {historyItem.total_doc_searches > 0 && (
            <Chip label={`${historyItem.total_doc_searches} doc searches`} size="small" color="secondary" variant="outlined" />
          )}
          {historyItem.total_charts > 0 && (
            <Chip label={`${historyItem.total_charts} charts`} size="small" color="success" variant="outlined" />
          )}
          <Chip
            label={historyItem.success ? "Success" : "Failed"}
            size="small"
            color={historyItem.success ? "success" : "error"}
          />
        </Box>

        {/* Question */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Question
          </Typography>
          <Box
            sx={{
              bgcolor: "action.hover",
              p: 2,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="body1">{historyItem.query}</Typography>
          </Box>
        </Box>

        {/* Response */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Response
          </Typography>
          <Box
            sx={{
              bgcolor: "background.paper",
              p: 2,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              "& p": { my: 1 },
              "& ul, & ol": { pl: 2 },
            }}
          >
            <ReactMarkdown>{historyItem.response}</ReactMarkdown>
          </Box>
        </Box>

        {/* SQL Queries */}
        {historyItem.sql_queries && historyItem.sql_queries.length > 0 && (
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">
                SQL Queries ({historyItem.sql_queries.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {historyItem.sql_queries.map((sq, i) => (
                <Box key={i} sx={{ mb: 2, "&:last-child": { mb: 0 } }}>
                  <Typography variant="caption" color="text.secondary">
                    Query {i + 1} - {sq.row_count} rows
                  </Typography>
                  {sq.sql && (
                    <Box
                      component="pre"
                      sx={{
                        bgcolor: "grey.900",
                        color: "grey.100",
                        p: 1.5,
                        borderRadius: 1,
                        overflow: "auto",
                        fontSize: "0.75rem",
                        mt: 0.5,
                      }}
                    >
                      {sq.sql}
                    </Box>
                  )}
                  {sq.summary && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
                      {sq.summary}
                    </Typography>
                  )}
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Doc Searches */}
        {historyItem.doc_searches && historyItem.doc_searches.length > 0 && (
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">
                Documentation Searches ({historyItem.doc_searches.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {historyItem.doc_searches.map((ds, i) => (
                <Box key={i} sx={{ mb: 1.5, "&:last-child": { mb: 0 } }}>
                  <Typography variant="body2" fontWeight={500}>
                    {ds.question}
                  </Typography>
                  {ds.table_names && ds.table_names.length > 0 && (
                    <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                      {ds.table_names.map((table, j) => (
                        <Chip key={j} label={table} size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Charts */}
        {historyItem.charts && historyItem.charts.length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">
                Charts ({historyItem.charts.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {historyItem.charts.map((chart, i) => (
                <Box key={i} sx={{ mb: 1, "&:last-child": { mb: 0 } }}>
                  <Typography variant="body2">
                    {chart.chart_type || "Chart"} -
                    <Chip
                      label={chart.status}
                      size="small"
                      color={chart.status === "success" ? "success" : "default"}
                      sx={{ ml: 1, fontSize: "0.7rem" }}
                    />
                  </Typography>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          startIcon={<ContentCopyIcon />}
          onClick={copyToClipboard}
          color={copySuccess ? "success" : "inherit"}
        >
          {copySuccess ? "Copied!" : "Copy as Markdown"}
        </Button>
        <Button startIcon={<DownloadIcon />} onClick={exportAsMarkdown}>
          Export
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={handleLoadToChat}>
          Load to Chat
        </Button>
      </DialogActions>
    </Dialog>
  );
};
